import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError, formatCHF, formatDate, formatTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import StarRating from "../components/StarRating";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { MapPin, Clock, Users, Calendar, ArrowLeft, CheckCircle, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { disciplineLabel } from "../lib/disciplines";

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "it";
  const [course, setCourse] = useState(null);
  const [instructor, setInstructor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [twintOpen, setTwintOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paid] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: c } = await api.get(`/courses/${id}`);
      setCourse(c);
      const { data: instr } = await api.get(`/instructors/${c.instructor_id}`);
      setInstructor(instr);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const book = async () => {
    if (!user) { navigate("/login", { state: { from: `/courses/${id}` } }); return; }
    if (user.role !== "client") { toast.error(t("course.only_clients_can_book")); return; }
    try {
      const { data } = await api.post("/bookings", { course_id: id });
      setBooking(data);
      setTwintOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmTwint = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-xs uppercase tracking-[0.3em] text-zinc-500">{t("common.loading")}</div>;
  if (!course) return <div className="text-center py-20">{t("course.not_found")}</div>;

  const seatsLeft = course.capacity - (course.bookings_count || 0);

  return (
    <div className="min-h-screen" data-testid="course-detail-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-2 mb-6" data-testid="back-button">
          <ArrowLeft size={14} /> {t("common.back")}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="aspect-[16/9] border border-white/10 overflow-hidden bg-[#0A0A0A] relative">
              <img src={course.image_url || "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600"}
                alt={course.title} className="w-full h-full object-cover grayscale" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute top-4 left-4 px-3 py-1 bg-[#E30613] text-white text-[10px] uppercase tracking-widest font-bold">
                {disciplineLabel(course.discipline, lang)}
              </div>
            </div>

            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{course.location}</div>
              <h1 className="font-display text-5xl uppercase font-black mt-2 tracking-tight" data-testid="course-title">{course.title}</h1>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat icon={Calendar} label={t("course.date")} value={formatDate(course.starts_at)} />
                <Stat icon={Clock} label={t("course.time")} value={`${formatTime(course.starts_at)} · ${course.duration_min} ${t("common.min")}`} />
                <Stat icon={MapPin} label={t("course.venue")} value={course.venue} />
                <Stat icon={Users} label={t("course.seats_label")} value={seatsLeft > 0 ? `${seatsLeft}/${course.capacity}` : t("common.complete")} />
              </div>
              {course.address && (
                <div className="mt-4 text-xs text-zinc-400 uppercase tracking-wider">{course.address}</div>
              )}
              <div className="mt-8 border-t border-white/10 pt-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{t("course.description")}</div>
                <p className="mt-3 text-zinc-300 leading-relaxed whitespace-pre-wrap" data-testid="course-description">{course.description}</p>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 self-start">
            <div className="border border-white/10 bg-[#0A0A0A] p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{t("course.price")}</div>
              <div className="font-display text-5xl font-black mt-2">{formatCHF(course.price_chf)}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-2">{t("course.pay_with_twint")}</div>

              {course.is_demo && (
                <div className="mt-6 border-l-2 border-amber-500 bg-amber-500/5 p-4" data-testid="course-demo-notice">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-amber-500">
                    <span className="w-1.5 h-1.5 bg-amber-500" /> {t("demo.badge_full")}
                  </div>
                  <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
                    {t("demo.notice_text")}
                  </p>
                </div>
              )}

              <Button
                onClick={book}
                disabled={seatsLeft <= 0 || course.is_demo}
                className="mt-6 w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12 btn-tactical disabled:opacity-50"
                data-testid="book-course-button"
              >
                {course.is_demo
                  ? t("demo.button_disabled")
                  : seatsLeft <= 0 ? t("course.sold_out") : t("course.book_now")}
              </Button>

              <div className="mt-6 pt-6 border-t border-white/10 space-y-2 text-xs text-zinc-400">
                <div className="flex justify-between"><span>{t("course.instant_confirm")}</span><CheckCircle size={14} className="text-[#00FF66]" weight="fill" /></div>
                <div className="flex justify-between"><span>{t("course.guaranteed_seat")}</span><CheckCircle size={14} className="text-[#00FF66]" weight="fill" /></div>
                <div className="flex justify-between"><span>{t("course.verified_instructor")}</span><CheckCircle size={14} className="text-[#00FF66]" weight="fill" /></div>
              </div>
            </div>

            {instructor && (
              <div className="border border-white/10 bg-[#0A0A0A] p-6 mt-4" data-testid="instructor-card">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{t("course.instructor")}</div>
                <div className="mt-2 font-display text-2xl uppercase font-black">{instructor.name}</div>
                <div className="mt-2 flex items-center gap-2">
                  <StarRating value={instructor.rating_avg || 0} testId="instructor-rating" />
                  <span className="text-xs text-zinc-400">{instructor.rating_avg || "0.0"} ({instructor.rating_count || 0})</span>
                </div>
                {instructor.bio && <p className="mt-3 text-xs text-zinc-400 line-clamp-4">{instructor.bio}</p>}
                <button onClick={() => navigate(`/instructors/${instructor.id}`)} className="mt-4 text-xs uppercase tracking-widest text-[#E30613] hover:text-white font-bold" data-testid="view-instructor-link">
                  {t("course.view_profile")} →
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>

      <Dialog open={twintOpen} onOpenChange={(o) => { setTwintOpen(o); if (!o) setBooking(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 rounded-none max-w-md" data-testid="twint-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl font-black">{t("course.twint_dialog_title")}</DialogTitle>
            <DialogDescription className="text-zinc-400">{t("course.twint_dialog_desc")}</DialogDescription>
          </DialogHeader>

          {booking && !paid && (
            <div className="space-y-4">
              <div className="border border-white/10 p-4 space-y-2 text-sm">
                <Row label={t("course.course_label")} value={booking.course_title} />
                <Row label={t("course.reference")} value={booking.twint_reference} />
                <Row label={t("course.total_price")} value={formatCHF(booking.price_chf)} bold />
                <div className="border-t border-white/10 pt-2 mt-2 text-[10px] uppercase tracking-wider text-zinc-500 space-y-1">
                  <Row label={t("course.platform_commission")} value={formatCHF(booking.commission_chf)} small />
                  <Row label={t("course.stripe_fee")} value={formatCHF(booking.stripe_fee_chf)} small />
                  <Row label={t("course.paid_to_instructor")} value={formatCHF(booking.instructor_payout_chf)} small />
                </div>
              </div>

              <div className="border-2 border-[#E30613] p-5 bg-black flex items-center gap-4">
                <div className="w-14 h-14 bg-[#E30613] flex items-center justify-center font-display font-black text-xl">T</div>
                <div>
                  <div className="font-display uppercase text-xl font-black">TWINT</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">{t("course.twint_subtitle")}</div>
                </div>
              </div>

              <Button onClick={confirmTwint} disabled={paying}
                className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12 btn-tactical"
                data-testid="twint-confirm-button">
                <CreditCard size={16} className="mr-2" />
                {paying ? t("course.redirecting") : t("course.pay_amount", { amount: formatCHF(booking.price_chf) })}
              </Button>

              <div className="text-[10px] text-zinc-500 uppercase tracking-wider text-center">{t("course.secure_payment")}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/10 p-3">
      <Icon size={16} className="text-[#E30613]" />
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-2">{label}</div>
      <div className="text-sm text-white font-semibold mt-1">{value}</div>
    </div>
  );
}

function Row({ label, value, bold, small }) {
  return (
    <div className="flex justify-between gap-2">
      <span className={small ? "text-zinc-500" : "text-zinc-400"}>{label}</span>
      <span className={`${bold ? "font-bold text-white" : "text-white"} ${small ? "text-zinc-400" : ""}`}>{value}</span>
    </div>
  );
}
